<template>
  <v-container fluid class="pa-4 pull-up">
    <!-- Configuration Section -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-medium">
            <v-icon left>mdi-cog</v-icon>
            {{ $t('item.label.label_configuration') }}
          </v-card-title>
          <v-card-text>
            <v-form ref="labelForm" v-model="formValid">
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.label_type"
                    :items="labelTypes"
                    item-text="label"
                    item-value="value"
                    :label="$t('item.label.label_type')"
                    :rules="[v => !!v || $t('item.validation.label_type_required')]"
                    outlined
                    dense
                    prepend-icon="mdi-label-outline"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.department"
                    :items="departments"
                    item-text="name"
                    item-value="id"
                    :label="$t('item.label.department')"
                    :rules="[v => !!v || $t('item.validation.department_required')]"
                    outlined
                    dense
                    prepend-icon="mdi-domain"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="form.barcode_source_field"
                    :items="barcodeSources"
                    item-text="label"
                    item-value="value"
                    :label="$t('item.label.barcode_source')"
                    :rules="[v => !!v || $t('item.validation.barcode_source_required')]"
                    outlined
                    dense
                    prepend-icon="mdi-barcode-scan"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-row>
                    <v-col cols="6">
                      <v-checkbox
                        v-model="form.print_name"
                        :label="$t('item.label.print_name')"
                        color="primary"
                        hide-details
                      />
                    </v-col>
                    <v-col cols="6">
                      <v-checkbox
                        v-model="form.print_price"
                        :label="$t('item.label.print_price')"
                        color="primary"
                        hide-details
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Item Selection Section -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-medium">
            <v-icon left>mdi-package-variant</v-icon>
            {{ $t('item.label.select_items') }}
          </v-card-title>
          <v-card-text>
            <v-row align="center" class="mb-4">
              <v-col cols="12" md="4">
                <v-btn
                  color="primary"
                  outlined
                  @click="showCategoryDialog = true"
                  block
                >
                  <v-icon left>mdi-folder-multiple</v-icon>
                  {{ $t('item.label.add_by_category') }}
                </v-btn>
              </v-col>
              <v-col cols="12" md="4">
                <v-btn
                  color="primary"
                  outlined
                  @click="showSupplierDialog = true"
                  block
                >
                  <v-icon left>mdi-truck</v-icon>
                  {{ $t('item.label.add_by_supplier') }}
                </v-btn>
              </v-col>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="selectedItem"
                  :items="searchResults" return-object
                  item-text="name"
                  item-value="id"
                  :label="$t('item.label.search_items')"
                  :search-input.sync="searchQuery"
                  :loading="searching"
                  @input="onItemSelect"
                  outlined
                  dense
                  prepend-icon="mdi-magnify"
                  clearable
                />
              </v-col>
            </v-row>

            <!-- Selected Items Table -->
            <v-data-table
              :headers="itemHeaders"
              :items="selectedItems"
              :items-per-page="10"
              class="elevation-1"
              dense
            >
              <template slot="item.quantity" slot-scope="{ item }">
                <v-text-field
                  v-model.number="item.quantity" type="number"
                  min="1"
                  dense flat
                  hide-details hide-spin-buttons
                  style="min-width: 100px" class="my-2 centered-input">
                  <template v-slot:prepend-inner>
                    <v-btn small depressed icon :disabled="item.quantity <= 1" @click="--item.quantity">
                      <v-icon small color="error">mdi-minus</v-icon>
                    </v-btn>
                  </template>
                  <template v-slot:append>
                    <v-btn small depressed icon @click="++item.quantity">
                      <v-icon small color="success">mdi-plus</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </template>
              <template slot="item.actions" slot-scope="{ item }">
                <v-btn
                  icon
                  small
                  color="error"
                  @click="removeItem(item)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-row class="mt-4">
              <v-col cols="6">
                <v-btn
                  color="info"
                  outlined
                  @click="addTestItems"
                  small
                >
                  <v-icon left>mdi-plus</v-icon>
                  {{ $t('item.label.add_test_items') }}
                </v-btn>
              </v-col>
              <v-col cols="6" class="text-right">
                <v-btn
                  color="primary"
                  :disabled="!canGenerateLabels"
                  :loading="generating"
                  @click="generateLabels"
                  large
                >
                  <v-icon left>mdi-printer</v-icon>
                  {{ $t('item.label.generate_labels') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Print Preview Section -->
    <v-row v-if="labels.length > 0">
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-medium">
            <v-icon left>mdi-eye</v-icon>
            {{ $t('item.label.print_preview') }}
            <v-spacer />
            <v-btn
              color="success"
              @click="printLabels"
              :loading="printing"
            >
              <v-icon left>mdi-printer</v-icon>
              {{ $t('item.label.print_labels') }}
            </v-btn>
          </v-card-title>
                     <v-card-text>
             <div class="print-container" ref="printContainer">
               <div class="labels-grid" :style="getGridStyle()">
                 <div
                   v-for="(label, index) in labels"
                   :key="`${label.id}-${index}`"
                   class="label-item"
                   :style="getLabelStyle()"
                 >
                   <div class="label-content">
                     <div v-if="form.print_price && label.price" class="label-price">
                       {{ formatPrice(label.price) }}
                     </div>
                     <div v-if="form.print_name && label.name" class="label-name">
                       {{ label.name }}
                     </div>
                      <div v-if="label.barcode" class="label-barcode">
                        <barcode
                          :value="label.barcode"
                          :options="barcodeOptions"
                          :key="`barcode-${label.id}-${index}`"
                          tag="svg"
                        />
                      </div>
                   </div>
                 </div>
               </div>
             </div>
           </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Category Selection Dialog -->
    <v-dialog v-model="showCategoryDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-folder-multiple</v-icon>
          {{ $t('item.label.select_categories') }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="categorySearch"
            :label="$t('item.label.search_categories')"
            outlined
            dense
            prepend-icon="mdi-magnify"
            clearable
          />
          <v-list dense style="max-height: 300px; overflow-y: auto">
            <v-list-item
              v-for="category in filteredCategories"
              :key="category.id"
              @click="toggleCategory(category)"
            >
              <v-list-item-action>
                <v-checkbox
                  v-model="category.selected"
                  color="primary"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ category.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showCategoryDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="addCategoryItems"
            :disabled="!hasSelectedCategories"
          >
            {{ $t('item.label.add_items') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Selection Dialog -->
    <v-dialog v-model="showSupplierDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-truck</v-icon>
          {{ $t('item.label.select_suppliers') }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="supplierSearch"
            :label="$t('item.label.search_suppliers')"
            outlined
            dense
            prepend-icon="mdi-magnify"
            clearable
          />
          <v-list dense style="max-height: 300px; overflow-y: auto">
            <v-list-item
              v-for="supplier in filteredSuppliers"
              :key="supplier.id"
              @click="toggleSupplier(supplier)"
            >
              <v-list-item-action>
                <v-checkbox
                  v-model="supplier.selected"
                  color="primary"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ supplier.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showSupplierDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="addSupplierItems"
            :disabled="!hasSelectedSuppliers"
          >
            {{ $t('item.label.add_items') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import VueBarcode from '@chenfengyuan/vue-barcode'

export default {
  name: 'LabelsNew',
  components: {
    Barcode: VueBarcode
  },
  data () {
    return {
      formValid: false,
      form: {
        label_type: '',
        department: '',
        barcode_source_field: '',
        print_name: true,
        print_price: true
      },
      labelTypes: [
        { label: 'Dymo - 3/4" x 2-1/2"', value: '19mm x 64mm' },
        { label: 'Dymo - 1" x 2-1/8"', value: '25mm x 54mm' },
        { label: 'Dymo - 1-1/8" x 3-1/2"', value: '28mm x 89mm' },
        { label: 'Zebra - 1-1/4" x 1"', value: '32mm x 25mm' },
        { label: 'Zebra - 2" x 1"', value: '51mm x 25mm' }
      ],
      barcodeSources: [
        { label: this.$t('item.label.sku'), value: 'code' },
        { label: this.$t('item.label.barcode'), value: 'barcode' },
        { label: this.$t('item.label.none'), value: 'none' }
      ],
      departments: [],
      categories: [],
      suppliers: [],
      selectedItems: [],
      labels: [],
      searchQuery: '',
      searchResults: [],
      searching: false,
      selectedItem: null,
      showCategoryDialog: false,
      showSupplierDialog: false,
      categorySearch: '',
      supplierSearch: '',
      generating: false,
      printing: false,
      itemHeaders: [
        { text: this.$t('item.label.item_name'), value: 'name', sortable: true },
        { text: this.$t('item.label.sku'), value: 'code', sortable: true },
        { text: this.$t('item.label.price_label'), value: 'unit_price', sortable: true },
        { text: this.$t('item.label.quantity'), value: 'quantity', sortable: false, width: '120px', align: 'center' },
        { text: this.$t('item.label.actions'), value: 'actions', sortable: false, width: '80px' }
      ]
    }
  },
  computed: {
    canGenerateLabels () {
      return this.formValid && this.selectedItems.length > 0
    },
    filteredCategories () {
      if (!this.categorySearch) return this.categories
      return this.categories.filter(cat =>
        cat.name.toLowerCase().includes(this.categorySearch.toLowerCase())
      )
    },
    filteredSuppliers () {
      if (!this.supplierSearch) return this.suppliers
      return this.suppliers.filter(sup =>
        sup.name.toLowerCase().includes(this.supplierSearch.toLowerCase())
      )
    },
    hasSelectedCategories () {
      return this.categories.some(cat => cat.selected)
    },
    hasSelectedSuppliers () {
      return this.suppliers.some(sup => sup.selected)
    },
    barcodeOptions () {
      return this.$labelPrintService.getBarcodeOptions(this.form.label_type)
    }
  },
  watch: {
    searchQuery: {
      handler: 'debounceSearch',
      immediate: false
    }
  },
  methods: {
    debounceSearch: function () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.searchItems()
      }, 300)
    },
    async searchItems () {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        this.searchResults = []
        return
      }

      this.searching = true
      try {
        const results = await this.$itemService.search({ name: this.searchQuery })
        this.searchResults = results
      } catch (error) {
        this.notify(this.$t('item.message.failed_search_items'), this.$t('status.error'), 'error')
        console.error('Search error:', error)
      } finally {
        this.searching = false
      }
    },
    onItemSelect (item) {
      console.log(item)
      if (item) {
        this.addItem(item)
        this.selectedItem = null
      }
    },
    addItem (item) {
      const existingItem = this.selectedItems.find(i => i.id === item.id)
      if (existingItem) {
        existingItem.quantity += 1
      } else {
        this.selectedItems.push({
          ...item,
          quantity: item.optimal_order || item.reorder_level || 1
        })
      }
    },
    removeItem (item) {
      const index = this.selectedItems.findIndex(i => i.id === item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      }
    },
    toggleCategory (category) {
      category.selected = !category.selected
    },
    toggleSupplier (supplier) {
      supplier.selected = !supplier.selected
    },
    async addCategoryItems () {
      const selectedCategories = this.categories.filter(cat => cat.selected)
      if (selectedCategories.length === 0) return

      try {
        const categoryIds = selectedCategories.map(cat => cat.id)
        const items = await this.$itemService.getCategoryItems(categoryIds)

        items.forEach(item => {
          if (!this.selectedItems.find(i => i.id === item.id)) {
            this.addItem(item)
          }
        })

        this.showCategoryDialog = false
        this.notify(this.$t('item.message.added_category_items', { count: items.length }), this.$t('status.success'), 'success')
      } catch (error) {
        this.notify(this.$t('item.message.failed_add_category_items'), this.$t('status.error'), 'error')
        console.error('Category items error:', error)
      }
    },
    async addSupplierItems () {
      const selectedSuppliers = this.suppliers.filter(sup => sup.selected)
      if (selectedSuppliers.length === 0) return

      try {
        const supplierIds = selectedSuppliers.map(sup => sup.id)
        const items = await this.$itemService.getSupplierItems(supplierIds)

        items.forEach(item => {
          if (!this.selectedItems.find(i => i.id === item.id)) {
            this.addItem(item)
          }
        })

        this.showSupplierDialog = false
        this.notify(this.$t('item.message.added_supplier_items', { count: items.length }), this.$t('status.success'), 'success')
      } catch (error) {
        this.notify(this.$t('item.message.failed_add_supplier_items'), this.$t('status.error'), 'error')
        console.error('Supplier items error:', error)
      }
    },
    generateLabels () {
      this.generating = true

      try {
        // Validate configuration
        const validation = this.$labelPrintService.validateConfig(this.form)
        if (!validation.valid) {
          this.notify(validation.errors.join(', '), this.$t('item.message.validation_error'), 'error')
          return
        }

        // Generate labels using the service
        this.labels = this.$labelPrintService.generateLabels(this.selectedItems, this.form)

        if (this.labels.length > 0) {
          this.notify(this.$t('item.message.generated_labels', { count: this.labels.length }), this.$t('status.success'), 'success')

          // Force barcode re-render after labels are generated
          this.$nextTick(() => {
            // Trigger barcode component updates
            this.$forceUpdate()
          })
        } else {
          this.notify(this.$t('item.message.no_labels_generated'), this.$t('status.warning'), 'warning')
        }
      } catch (error) {
        this.notify(this.$t('item.message.failed_generate_labels'), this.$t('status.error'), 'error')
        console.error('Generate labels error:', error)
      } finally {
        this.generating = false
      }
    },
    getLabelStyle () {
      if (!this.form.label_type) {
        return {
          width: '200px',
          height: '80px',
          minWidth: '200px',
          minHeight: '80px'
        }
      }

      const dimensions = this.form.label_type.split(' x ')
      if (dimensions.length === 2) {
        const width = dimensions[0].replace('mm', '')
        const height = dimensions[1].replace('mm', '')
        return {
          width: `${width}mm`,
          height: `${height}mm`,
          minWidth: `${width}mm`,
          minHeight: `${height}mm`,
          maxWidth: `${width}mm`,
          maxHeight: `${height}mm`
        }
      }
      return {
        width: '200px',
        height: '80px',
        minWidth: '200px',
        minHeight: '80px'
      }
    },
    getGridStyle () {
      if (!this.form.label_type) {
        return {
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'
        }
      }

      const layout = this.$labelPrintService.calculateLayout(this.form.label_type)
      return {
        gridTemplateColumns: `repeat(${layout.columns}, 1fr)`,
        gap: `${layout.gap}mm`
      }
    },
    formatPrice (price) {
      return this.$labelPrintService.formatPrice(price, 'XAF')
    },
    async printLabels () {
      this.printing = true
      console.log('Starting printLabels method')

      try {
        // First, ensure the print container is visible
        const printContainer = this.$refs.printContainer
        if (!printContainer) {
          throw new Error('Print container not found')
        }
        console.log('Print container found:', printContainer)

        // Make sure the container is visible for html2canvas
        printContainer.style.display = 'block'
        console.log('Print container display set to block')

        // Use html2canvas to capture the labels as an image
        console.log('Starting html2canvas capture')
        const canvas = await this.$html2canvas(printContainer)
        console.log('html2canvas capture successful')

        // Create a hidden div to hold the image for printing
        let printDiv = document.getElementById('labels-sheet')
        if (!printDiv) {
          printDiv = document.createElement('div')
          printDiv.id = 'labels-sheet'
          printDiv.style.position = 'fixed'
          printDiv.style.top = '0'
          printDiv.style.left = '0'
          printDiv.style.zIndex = '-9999'
          document.body.appendChild(printDiv)
          console.log('Created new print div with fixed positioning')
        } else {
          console.log('Using existing print div')
        }

        // Set the image source with proper styling
        const imgSrc = canvas.toDataURL('image/png')
        printDiv.innerHTML = '<div style="width: 100%; text-align: center;"><img src="' + imgSrc + '" style="max-width: 100%;" /></div>'
        console.log('Image added to print div with proper styling')

        // Add a small delay before printing to ensure content is fully rendered
        await new Promise(resolve => setTimeout(resolve, 300))

        // Print using htmlToPaper with explicit options
        console.log('Starting htmlToPaper print')
        await this.$htmlToPaper('labels-sheet', {
          name: '_blank',
          specs: ['fullscreen=yes', 'titlebar=yes', 'scrollbars=yes'],
          timeout: 0,
          autoClose: false,
          windowTitle: 'Print Labels'
        }, (done) => {
          console.log('Printing completed callback. Success:', done)
        })
        console.log('htmlToPaper print initiated')

        this.notify(this.$t('item.message.print_dialog_opened'), this.$t('status.success'), 'success')
        console.log('Print process completed successfully')
      } catch (error) {
        console.error('Main print method error:', error)

        // Fallback to the service method if html2canvas fails
        try {
          console.log('Attempting fallback print method')
          await this.$labelPrintService.printLabels(this.labels, this.form)
          this.notify(this.$t('item.message.print_dialog_opened'), this.$t('status.success'), 'success')
          console.log('Fallback print method successful')
        } catch (fallbackError) {
          console.error('Fallback print error:', fallbackError)
          this.notify(this.$t('item.message.failed_print_labels', { message: error.message }), this.$t('status.error'), 'error')
          console.error('Print error:', error)
        }
      } finally {
        this.printing = false
        console.log('Print process finished, printing state reset')
      }
    },
    async loadInitialData () {
      try {
        const [departments, categories, suppliers] = await Promise.all([
          this.$departmentService.getDepartments(),
          this.$categoryService.getCategories(),
          this.$supplierService.getSuppliers()
        ])

        this.departments = departments
        this.categories = categories.map(cat => ({
          ...cat,
          selected: false
        }))
        this.suppliers = suppliers.map(sup => ({
          ...sup,
          selected: false
        }))

        // Add some test data if no departments exist
        if (!this.departments || this.departments.length === 0) {
          this.departments = [
            {
              id: 1,
              name: 'Test Department'
            }
          ]
        }

        // Set default values for testing
        this.form.department = this.departments[0]?.id || ''
        this.form.label_type = '25mm x 54mm'
        this.form.barcode_source_field = 'code'
      } catch (error) {
        this.notify(this.$t('item.message.failed_load_initial_data'), this.$t('status.error'), 'error')
        console.error('Initial data error:', error)
      }
    },
    addTestItems () {
      // Add some test items for demonstration
      const testItems = [
        {
          id: 1,
          name: 'Test Product 1',
          code: 'TP001',
          barcode: '1234567890123',
          unit_price: 1500,
          optimal_order: 1
        },
        {
          id: 2,
          name: 'Test Product 2',
          code: 'TP002',
          barcode: '9876543210987',
          unit_price: 2500,
          optimal_order: 2
        }
      ]

      testItems.forEach(item => {
        this.addItem(item)
      })

      this.notify(this.$t('item.message.added_test_items'), this.$t('status.info'), 'info')
    },
    async loadPurchaseOrderItems (poId) {
      try {
        // Fetch the purchase order data
        const purchaseOrder = await this.$supplyService.fetch(poId)

        if (!purchaseOrder || !purchaseOrder.received || purchaseOrder.received.length === 0) {
          this.notify(this.$t('item.message.no_received_items'), this.$t('status.warning'), 'warning')
          return
        }

        // Extract all received items from the purchase order
        const receivedItems = purchaseOrder.received.flatMap(receipt => receipt.items)
        console.log(receivedItems)
        // Group by item ID and sum quantities
        const itemQuantities = {}
        receivedItems.forEach(item => {
          if (!itemQuantities[item.item_id]) {
            itemQuantities[item.item_id] = 0
          }
          itemQuantities[item.item_id] += item.quantity
        })

        // Fetch full item details for each received item
        const itemIds = Object.keys(itemQuantities)
        const items = await this.$itemService.queryItems(itemIds)

        // Add items to the selected items list with their received quantities
        items.forEach(item => {
          if (itemQuantities[item.id]) {
            this.addItem({
              ...item,
              optimal_order: itemQuantities[item.id] // Use received quantity
            })
          }
        })

        this.notify(this.$t('item.message.loaded_po_items', { count: items.length }), this.$t('status.success'), 'success')
      } catch (error) {
        this.notify(this.$t('item.message.failed_load_po_items'), this.$t('status.error'), 'error')
        console.error('Purchase order items error:', error)
      }
    }
  },
  mounted () {
    this.$emit('parent-title', this.$t('item.title.labels'))
    this.loadInitialData()

    // Check if we have a purchase order ID in the query parameters
    const poId = this.$route.query.po_id
    if (poId) {
      this.loadPurchaseOrderItems(poId)
    }
  }
}
</script>

<style scoped>
.centered-input >>> input {
  text-align: center
}

.print-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.labels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 10px;
  align-items: start;
  justify-items: center;
}

.label-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  page-break-inside: avoid;
  box-sizing: border-box;
  overflow: hidden;
}

.label-content {
  text-align: center;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.label-price {
  font-size: 14px;
  font-weight: bold;
  color: #1976d2;
  margin-bottom: 4px;
  flex-shrink: 0;
}

.label-name {
  font-size: 12px;
  margin-bottom: 6px;
  word-wrap: break-word;
  line-height: 1.2;
  flex-shrink: 0;
  max-width: 100%;
}

.label-barcode {
  text-align: center;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.label-barcode canvas {
  max-width: 100%;
  max-height: 100%;
  height: auto;
}

@media print {
  @page {
    margin: 10mm;
    size: A4;
  }

  body {
    margin: 0;
    padding: 0;
  }

  /* Hide all Vuetify components */
  .v-container,
  .v-card,
  .v-card__title,
  .v-card__actions,
  .v-btn,
  .v-dialog,
  .v-overlay,
  .v-row,
  .v-col {
    display: none !important;
  }

  /* Show only the print container */
  .print-container {
    display: block !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
    position: static !important;
  }

  .print-container * {
    display: block !important;
    visibility: visible !important;
  }

  .labels-grid {
    display: grid !important;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
    gap: 5mm !important;
    width: 100% !important;
  }

  .label-item {
    border: 1px solid #000 !important;
    padding: 2mm !important;
    margin: 0 !important;
    page-break-inside: avoid !important;
  }

  .label-content {
    text-align: center !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: space-between !important;
    align-items: center !important;
    height: 100% !important;
  }

  .label-price {
    font-size: 12pt !important;
    font-weight: bold !important;
    margin-bottom: 1mm !important;
  }

  .label-name {
    font-size: 10pt !important;
    margin-bottom: 2mm !important;
    word-wrap: break-word !important;
  }

  .label-barcode {
    text-align: center !important;
    flex-grow: 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .label-barcode canvas {
    max-width: 100% !important;
    max-height: 100% !important;
    height: auto !important;
  }
}
</style>
